import React, { useMemo } from 'react';
import { RouteProp } from '@react-navigation/native';
import { ProfilesTabParamList } from '../../../../../utils/appNavigationNames';
import { useCollaboratorInvoices } from '../../../../../hooks/api/useCollaboratorInvoices';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import Text from '../../../../v2BaseComponents/Text';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import Stack, { ContentStack } from '../../../../v2BaseComponents/Stack';
import { getIntl } from '../../../../../utils/intl';
import CollaboratorCard from '../../../../shared/CollaboratorCard';
import LabelValueDisplay, { LabelValueItem } from '../../../../v2BaseComponents/LabelValueDisplay';
import { centsToDollarsConversion } from '../../../../../utils/centsToDollarsConversion';
import getDateString from '../../../../../utils/getDateString';
import { isPresent } from 'ts-is-present';
import { collaboratorInvoiceStatusTypeGuards } from '../../../../../utils/invoiceStatusTypeGuards';
import * as Linking from 'expo-linking';

const intl = getIntl('collaborator-invoice-screen', {
    invoiceFrom: 'Invoice from',
    open: 'Open',
    paid: 'Paid',
    void: 'Voided',
    status: 'Status',
    markPaid: 'Marked as Paid?',
    yes: 'Yes',
    no: 'No',
    paidDateTime: 'Paid Date and Time',
    voidedDateTime: 'Voided Date and Time',
    amount: 'Amount',
    createdDateTime: 'Created Date and Time',
    dueDateTime: 'Due Date and Time',
    description: 'Description',
    pay: 'Pay Invoice',
    view: 'View Invoice',
    refunded: 'Refunded',
});

export type CollaboratorInvoiceScreenOwnProps = {};

export type CollaboratorInvoiceScreenProps = CollaboratorInvoiceScreenOwnProps &
    CollaboratorInvoiceScreenLandingNavigatorProps;

export type CollaboratorInvoiceScreenLandingNavigatorProps = {
    route: RouteProp<ProfilesTabParamList, 'collaborator-invoice'>;
};

const CollaboratorInvoiceScreenScreen: React.FC<CollaboratorInvoiceScreenProps> = (props) => {
    const { collaboratorId, profileId, invoiceId } = props.route.params;
    const { isLoadingInvoice, invoice } = useCollaboratorInvoices({
        medicalProfileId: profileId,
        collaboratorId,
        isOrganizationCollaborator: true,
        invoiceId,
    });

    const labeledValues: LabelValueItem[] = useMemo<LabelValueItem[]>(
        () =>
            invoice
                ? [
                      {
                          label: intl('status'),
                          value: intl(invoice.invoice.status),
                      },
                      {
                          label: intl('amount'),
                          value: `$${centsToDollarsConversion.convertCentsToDollars(invoice.invoice.amount)}`,
                      },
                      {
                          label: intl('description'),
                          value: invoice.invoice.description,
                      },
                      {
                          label: intl('createdDateTime'),
                          value: getDateString(invoice.invoice.createdDateTime, 'date-time'),
                      },
                      {
                          label: intl('dueDateTime'),
                          value: getDateString(invoice.invoice.createdDateTime, 'date'),
                      },
                      collaboratorInvoiceStatusTypeGuards.paid(invoice.invoice)
                          ? {
                                label: intl('paidDateTime'),
                                value: getDateString(invoice.invoice.paidDateTime, 'date-time'),
                            }
                          : collaboratorInvoiceStatusTypeGuards.void(invoice.invoice)
                          ? {
                                label: intl('voidedDateTime'),
                                value: getDateString(invoice.invoice.voidDateTime, 'date-time'),
                            }
                          : null,
                      collaboratorInvoiceStatusTypeGuards.paid(invoice.invoice)
                          ? {
                                label: intl('markPaid'),
                                value: invoice.invoice.markedPaid ? intl('yes') : intl('no'),
                            }
                          : null,
                  ].filter(isPresent)
                : [],
        [invoice],
    );

    return (
        <AnimateLoadedContent isLoading={isLoadingInvoice}>
            {() => (
                <ActionButtonLayout
                    scrollable
                    actions={
                        invoice
                            ? [
                                  collaboratorInvoiceStatusTypeGuards.open(invoice.invoice)
                                      ? {
                                            title: intl('pay'),
                                            onPress: () => {
                                                if (collaboratorInvoiceStatusTypeGuards.open(invoice!.invoice)) {
                                                    Linking.openURL(invoice!.invoice.paymentUrl!);
                                                }
                                            },
                                            icon: 'CreditCard' as const,
                                            testID: 'pay-invoice-button',
                                        }
                                      : null,
                                  {
                                      title: intl('view'),
                                      onPress: () => {
                                          if (invoice) {
                                              Linking.openURL(invoice.invoice.pdfUrl);
                                          }
                                      },
                                      icon: 'Transaction' as const,
                                      variant: 'secondary' as const,
                                  },
                              ].filter(isPresent)
                            : []
                    }
                >
                    <MainContentContainer>
                        <ContentStack>
                            <Stack vertical align="flex-start" fullWidth>
                                <Text variant="body-1">{intl('invoiceFrom')}</Text>
                                <CollaboratorCard collaboratorId={collaboratorId} medicalProfileId={profileId} />
                            </Stack>
                            <LabelValueDisplay items={labeledValues} />
                        </ContentStack>
                    </MainContentContainer>
                </ActionButtonLayout>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(CollaboratorInvoiceScreenScreen);
